import { memo, useState, useCallback, useEffect, useMemo } from 'react'
import type { Program, LogoProfile } from '../../types/index.js'
import type { ProgramCategory } from '../../types/domain.js'
import type { ProbeResult } from '../../lib/mediaBrowserApi.js'
import { useProgramActions } from '../../hooks/usePrograms.js'
import { logoProfilesApi, programCategoriesApi } from '../../lib/api.js'
import { formatDuration, formatFileSize, NO_LOGO_VALUE } from '../../lib/utils.js'
import { draftProgramFromFileName, isFourThreeResolution } from '../../lib/media-program-draft.js'
import { programLogoProfileId } from '../../lib/program-logo-selection.js'
import { ModalShell } from '../common/ModalShell.js'
import './ProgramsView.css'

// ─── Props ────────────────────────────────────────────────────────────────────

interface MediaProgramImportModalProps {
  channelId: string
  /** Analyse FFprobe du fichier choisi dans le navigateur de médias. */
  media:     ProbeResult
  onCreated: (p: Program) => void
  onClose:   () => void
}

// ─── Composant ────────────────────────────────────────────────────────────────

/**
 * Fiche pré-remplie à partir d'un fichier média, à confirmer.
 *
 * Le média n'est associé qu'après création du programme : tant que l'opérateur
 * n'a pas confirmé, rien n'est écrit au catalogue. Les champs proposés viennent
 * du nom de fichier et de l'analyse FFprobe, et restent tous modifiables.
 */
export const MediaProgramImportModal = memo(function MediaProgramImportModal({
  channelId, media, onCreated, onClose,
}: MediaProgramImportModalProps) {
  const { createProgram, addMedia } = useProgramActions()
  const draft = useMemo(() => draftProgramFromFileName(media.fileName), [media.fileName])

  const [visible, setVisible] = useState(false)
  const [saving,  setSaving]  = useState(false)
  const [error,   setError]   = useState<string | null>(null)

  const [title,       setTitle]       = useState(draft.title)
  const [programCode, setProgramCode] = useState(draft.programCode)
  const [programCategoryId, setProgramCategoryId] = useState('')
  const [categories,  setCategories]  = useState<ProgramCategory[]>([])
  const [genre,       setGenre]       = useState('')
  const [durationSec, setDurationSec] = useState(Math.max(1, Math.round(media.durationSec ?? 0)))
  const [isFourThree, setIsFourThree] = useState(isFourThreeResolution(media.resolution))
  const [logoProfiles,  setLogoProfiles]  = useState<LogoProfile[]>([])
  const [logoSelection, setLogoSelection] = useState({ logoProfileId: '', isLive: false, isNew: true })
  const { logoProfileId, isNew } = logoSelection
  const setLogoProfileId = useCallback((value: string) => {
    setLogoSelection(current => ({ ...current, logoProfileId: value }))
  }, [])

  // Animation d'entrée
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 10)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    Promise.all([logoProfilesApi.list(channelId), programCategoriesApi.list({ channelId })])
      .then(([profiles, cats]) => {
        setLogoProfiles(profiles)
        setLogoSelection(current => ({ ...current, logoProfileId: programLogoProfileId(profiles, current) }))
        setCategories(cats)
      })
      .catch(console.error)
  }, [channelId])

  const changeIsNew = useCallback((value: boolean) => {
    setLogoSelection(current => {
      const next = { ...current, isNew: value }
      return { ...next, logoProfileId: programLogoProfileId(logoProfiles, next, true) }
    })
  }, [logoProfiles])

  const handleClose = useCallback(() => {
    setVisible(false)
    setTimeout(onClose, 200)
  }, [onClose])

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim() || !logoProfileId) return
    setSaving(true)
    setError(null)
    try {
      const program = await createProgram({
        channelId,
        title:             title.trim(),
        type:              'EMISSION',
        programCategoryId: programCategoryId || null,
        programCode:       programCode.trim() ? programCode.trim().toUpperCase() : null,
        durationSec:       Math.max(1, durationSec),
        interPartBreakSec: 0,
        additionalParts:   [],
        genre:             genre.trim() || undefined,
        // « Aucun logo » ne désigne aucun profil : le serveur retiendra celui
        // par défaut, que `noLogo` neutralise à l'antenne.
        logoProfileId:     logoProfileId === NO_LOGO_VALUE ? undefined : logoProfileId,
        noLogo:            logoProfileId === NO_LOGO_VALUE,
        isLive:            false,
        isFourThree,
        isNew,
        tags:              [],
      })
      // Le programme existe désormais : si l'association échoue, on remonte
      // l'erreur mais on garde la fiche, le média se rattache depuis la fiche.
      await addMedia(program.id, {
        filePath:      media.filePath,
        fileName:      media.fileName,
        codec:         media.codec         ?? undefined,
        container:     media.container     ?? undefined,
        durationSec:   media.durationSec   ?? undefined,
        resolution:    media.resolution    ?? undefined,
        fileSizeBytes: media.fileSizeBytes ?? undefined,
        isProxy:       false,
      })
      onCreated(program)
    } catch (err: any) {
      setError(err.message ?? 'Erreur lors de la création')
      setSaving(false)
    }
  }, [channelId, title, programCode, programCategoryId, genre, durationSec, isFourThree, isNew,
      logoProfileId, media, createProgram, addMedia, onCreated])

  return (
    <ModalShell title="Importer un média" visible={visible} onClose={handleClose}>
      <form onSubmit={handleSubmit} className="tp-modal-form">
        {error && (
          <div className="tp-feedback tp-feedback--error" role="alert">
            {error}
          </div>
        )}

        {/* Ce que le fichier dit de lui-même — non modifiable */}
        <div className="catalog-media-card">
          <div className="catalog-media-row__layout">
            <div className="catalog-media-row__copy">
              <div className="catalog-media-row__name">{media.fileName}</div>
              <div className="catalog-media-row__meta">
                <span>{media.durationSec ? formatDuration(Math.round(media.durationSec)) : 'Durée inconnue'}</span>
                <span>{media.resolution ?? 'Résolution inconnue'}</span>
                <span>{[media.container, media.codec].filter(Boolean).join(' · ') || 'Format inconnu'}</span>
                <span>{formatFileSize(media.fileSizeBytes)}</span>
              </div>
              <div className="catalog-media-draft__path">{media.filePath}</div>
            </div>
          </div>
        </div>

        {media.status !== 'OK' && (
          <div className="tp-feedback tp-feedback--warning" role="status">
            Analyse incomplète du fichier{media.error ? ` : ${media.error}` : ''}. Vérifiez la durée avant de confirmer.
          </div>
        )}

        <FormField label="Titre *">
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Titre du programme"
            required
            autoFocus
            className="tp-input"
          />
        </FormField>

        <div className="tp-form-grid">
          <FormField label="Type">
            <select value={programCategoryId} onChange={e => setProgramCategoryId(e.target.value)} className="tp-select">
              <option value="">— Aucun type —</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Durée (secondes)">
            <div className="catalog-duration-input">
              <input
                type="number"
                min={1}
                value={durationSec}
                onChange={e => setDurationSec(parseInt(e.target.value) || 0)}
                className="tp-input"
              />
              <span className="catalog-duration-input__formatted">= {formatDuration(durationSec)}</span>
            </div>
          </FormField>
        </div>

        <FormField label="Genre">
          <input
            value={genre}
            onChange={e => setGenre(e.target.value)}
            placeholder="Documentaire, Émission, Sport…"
            className="tp-input"
          />
        </FormField>

        <FormField label="Code programme">
          <input
            value={programCode}
            onChange={e => setProgramCode(e.target.value.toUpperCase())}
            placeholder="EX: JTLOCAL"
            className="tp-input tp-input--data"
          />
        </FormField>

        <FormField label="Habillage logo">
          <select value={logoProfileId} onChange={e => setLogoProfileId(e.target.value)} disabled={!logoProfiles.length} required className="tp-select">
            {!logoProfileId && <option value="" disabled>Aucun profil automatique disponible</option>}
            {logoProfiles.filter(profile => profile.active).map(profile => (
              <option key={profile.id} value={profile.id}>{profile.name}</option>
            ))}
            <option value={NO_LOGO_VALUE}>Aucun logo (le fichier en porte déjà un)</option>
          </select>
        </FormField>

        <FormField label="Format image">
          <AspectRatioSelector isFourThree={isFourThree} onChange={setIsFourThree} />
        </FormField>

        <label className="tp-check-row" htmlFor="importIsNew">
          <input
            type="checkbox"
            id="importIsNew"
            checked={isNew}
            onChange={e => changeIsNew(e.target.checked)}
          />
          <span>Marquer comme inédit</span>
        </label>

        <div className="tp-modal-actions">
          <button type="button" onClick={handleClose} className="tp-button">
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving || !title.trim() || !logoProfileId}
            className="tp-button tp-button--info"
            data-state={saving ? 'loading' : undefined}
          >
            {saving ? 'Création…' : 'Confirmer l’ajout'}
          </button>
        </div>
      </form>
    </ModalShell>
  )
})

// ─── Helpers ──────────────────────────────────────────────────────────────────

function FormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="tp-form-field">
      <span className="tp-field-label">{label}</span>
      {children}
    </label>
  )
}

function AspectRatioSelector({ isFourThree, onChange }: { isFourThree: boolean; onChange: (value: boolean) => void }) {
  const options = [
    { label: '16/9', description: 'Plein cadre', value: false },
    { label: '4/3', description: 'Pillarbox', value: true },
  ]
  return (
    <div className="catalog-aspect-options">
      {options.map(option => {
        const active = isFourThree === option.value
        return (
          <button
            key={option.label}
            type="button"
            onClick={() => onChange(option.value)}
            className={`catalog-aspect-option${active ? ' is-active' : ''}`}
            aria-pressed={active}
          >
            <span className="catalog-aspect-option__title">{option.label}</span>
            <span className="catalog-aspect-option__description">{option.description}</span>
          </button>
        )
      })}
    </div>
  )
}
